import { Check, X } from "lucide-react"
import { Button } from "../../../components/button"
import { PERMIT_TYPES, STATUS_IDS, USER_ROLES_IDS } from "../../../constants/common"
import useAuthStore from "../../../store/Auth"
import { memo, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import ApprovePermitModal from "../../../components/modal/ApprovePermitModal"
import RejectPermitModal from "../../../components/modal/RejectPermitModal"
import toast from "react-hot-toast"
import { useAddUpdateOfficeAccessAuthorization } from "../../../hooks/permit/mutation"
import { APP_ROUTES } from "../../../routes"
import { useLocation, useNavigate } from "react-router-dom"
import { UseFormHandleSubmit } from "react-hook-form"

interface FormData {
    name: string;
    department: string;
    mobileNo: string;
    officeNumber: string;
    employeeNumber: string;
}

interface ActionButtonGroupProps {
    officeAccessAuthorization: any;
    isFieldsDisabled: boolean;
    isPermitApprovedByNextApprover: boolean;
    requestedServicesIds: number[];
    handleSubmit: UseFormHandleSubmit<{
        name: string;
        department: string;
        mobileNo: string;
        officeNumber: string;
        employeeNumber: string;
    }, undefined>
}

const ActionButtonGroup = ({
    officeAccessAuthorization,
    isFieldsDisabled,
    handleSubmit,
    isPermitApprovedByNextApprover,
    requestedServicesIds,
}: ActionButtonGroupProps) => {
    const { user } = useAuthStore()

    const { t } = useTranslation()

    const navigate = useNavigate()

    const location = useLocation()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const [isApprovePermitModalOpen, setIsApprovePermitModalOpen] =
        useState(false);
    const [isRejectAppointmentModalOpen, setIsRejectAppointmentModalOpen] =
        useState(false);

    const isApprovePage = useMemo(
        () => location.pathname === APP_ROUTES.APPROVE_OFFICE_ACCESS_AUTHORIZATION,
        [location.pathname],
    );

    const {
        mutateAsync: AddUpdateOfficeAccessAuthorization,
        isPending: isAddUpdateOfficeAccessAuthorizationLoading,
    } = useAddUpdateOfficeAccessAuthorization();

    const isApproveDisabled = useMemo(
        () =>
            officeAccessAuthorization?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            officeAccessAuthorization?.n_ApprovalStatus === STATUS_IDS.APPROVED ||
            officeAccessAuthorization?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            officeAccessAuthorization?.n_ApprovalStatus === STATUS_IDS.REJECTED ||
            // not fully approved -> only current approver can approve
            (officeAccessAuthorization?.n_ApprovalStatus !== STATUS_IDS.APPROVED &&
                user?.n_RoleID !== officeAccessAuthorization?.n_PermitNextApproverRoleID),
        [
            officeAccessAuthorization?.n_ApprovalStatus,
            officeAccessAuthorization?.n_PermitNextApproverRoleID,
            user?.n_RoleID,
        ]
    );

    const onApprove = useCallback(
        async () => {
            if (isApproveDisabled) return;

            setIsApprovePermitModalOpen(true);
        },
        [isApproveDisabled, t]
    );

    const isRejectDisabled = useMemo(
        () =>
            officeAccessAuthorization?.n_ApprovalStatus === STATUS_IDS.EXPIRED ||
            officeAccessAuthorization?.n_ApprovalStatus === STATUS_IDS.CANCELED ||
            officeAccessAuthorization?.n_ApprovalStatus === STATUS_IDS.REJECTED,
        [officeAccessAuthorization?.n_ApprovalStatus]
    );

    const onReject = useCallback(async () => {
        if (isRejectDisabled) return;

        setIsRejectAppointmentModalOpen(true);
    }, [isRejectDisabled, officeAccessAuthorization?.permitEndDate, t]);

    const onSubmit = useCallback(async (data: FormData) => {
        if (requestedServicesIds.length === 0) {
            toast.error(t("pleaseSelectAtLeastOneService"));
            return;
        }

        try {
            const body = {
                n_OfficeAccessAuthorisationRequestID: key ? Number(key) : 0,
                s_EmployeeName: data.name,
                s_Department: data.department,
                s_MobileNumber: data.mobileNo,
                s_EmployeeNumber: data.employeeNumber,
                s_OfficeNumber: data.officeNumber,
                requestedServices: requestedServicesIds,
            }

            await AddUpdateOfficeAccessAuthorization(body);

            toast.success(
                key
                    ? t("officeAccessAuthorizationRequestUpdatedSuccessfully")
                    : t("officeAccessAuthorizationRequestedSuccessfully")
            );

            setTimeout(() => {
                navigate(APP_ROUTES.SEARCH_PERMIT_REQUEST);
            }, 700);
        } catch (error) {
            toast.error(t("somethingWentWrong"));
        }
    }, [
        requestedServicesIds,
        key,
        navigate,
        t,
        AddUpdateOfficeAccessAuthorization
    ])

    return (
        <>
            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {key && (
                        <>
                            {isApprovePage ? (
                                <>
                                    <Button
                                        onClick={handleSubmit(onApprove)}
                                        Icon={<Check className="w-4" />}
                                        className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                        disabled={
                                            user?.n_RoleID === USER_ROLES_IDS.STAFF ||
                                            isApproveDisabled
                                        }
                                    >
                                        {t("approve")}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onReject}
                                        className="bg-red-600 hover:bg-red-700"
                                        disabled={isRejectDisabled}
                                        Icon={<X className="w-4" />}
                                    >
                                        {t("reject")}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleSubmit(onSubmit)}
                                    isLoading={isAddUpdateOfficeAccessAuthorizationLoading}
                                    disabled={
                                        isAddUpdateOfficeAccessAuthorizationLoading ||
                                        isPermitApprovedByNextApprover ||
                                        (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                                            isFieldsDisabled)
                                    }
                                    className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                                >
                                    {t("update")}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}

            {user?.n_RoleID !== USER_ROLES_IDS.ADMIN && (
                <div className="flex justify-end gap-1">
                    {!key && (
                        <Button
                            onClick={handleSubmit(onSubmit)}
                            isLoading={isAddUpdateOfficeAccessAuthorizationLoading}
                            disabled={
                                isAddUpdateOfficeAccessAuthorizationLoading
                            }
                            className="dark:bg-white dark:text-purple-primary hover:dark:text-purple-primary hover:dark:bg-white/80"
                        >
                            {t("submit")}
                        </Button>
                    )}
                </div>
            )}

            <ApprovePermitModal
                type={PERMIT_TYPES.OFFICE_ACCESS_AUTHORIZATION}
                permitId={officeAccessAuthorization?.n_OfficeAccessAuthorisationRequestID || 0}
                isOpen={isApprovePermitModalOpen}
                onOpenChange={() => setIsApprovePermitModalOpen(false)}
            />

            <RejectPermitModal
                type={PERMIT_TYPES.OFFICE_ACCESS_AUTHORIZATION}
                permitId={officeAccessAuthorization?.n_OfficeAccessAuthorisationRequestID || 0}
                isOpen={isRejectAppointmentModalOpen}
                onOpenChange={() => setIsRejectAppointmentModalOpen(false)}
            />
        </>
    )
}

export default memo(ActionButtonGroup)
